import React, { useEffect, useMemo, useState, useRef } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../../components/ConfirmProvider";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Car as CarIcon, CalendarDays, Search, AlertTriangle, CheckCircle2, Layers, Plus, Send, X, Trash2, Pencil } from "lucide-react";
import html2canvas from "html2canvas";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  DAY_NAMES, MONTH_NAMES,
  COLORS, PROJECT_INACTIVE_CATEGORIES, DISMANTLE_WINDOW_DAYS,
  toISO, mondayOf, addDays, fmtRoDay, dowFromIso, isSaturdayIso, fmtRangeLabel, INVENTORY_SET_COLORS,
} from "./utils";
import { fmtRoDate } from "../../lib/dateFmt";

/* eslint-disable no-unused-vars */

export default function AssignDialog({ entry, persons, cars, weekStart, regiiWeeks = [], onClose, onSaved }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const canDeleteSource = user?.role === "admin" || user?.role === "planificator";
  const [selectedPersons, setSelectedPersons] = useState(entry.person_ids || []);
  const [selectedCars, setSelectedCars] = useState(entry.car_ids || []);
  const [note, setNote] = useState(entry.note || "");
  const [allocColor, setAllocColor] = useState(entry.allocation_color || "");
  const [saving, setSaving] = useState(false);
  const [qp, setQp] = useState("");
  const [qc, setQc] = useState("");
  const [project, setProject] = useState(null);
  const [regie, setRegie] = useState(null);
  // Per-week override pentru regii
  const isRegieEntry = !!entry.is_regie && !!entry.regie_id;
  const existingWeek = isRegieEntry ? (regiiWeeks || []).find((w) => w.regie_id === entry.regie_id) : null;
  const [weekMinPeople, setWeekMinPeople] = useState(existingWeek?.min_people ?? "");
  const [savingWeek, setSavingWeek] = useState(false);

  // Per-DAY override (pentru regii ȘI proiecte) pe ziua pe care s-a click-uit cardul
  const clickedDay = entry._clickedDay || null;
  const [dayMinPeople, setDayMinPeople] = useState("");
  const [savingDay, setSavingDay] = useState(false);
  const [dayOverrideExists, setDayOverrideExists] = useState(false);
  const [dayDefault, setDayDefault] = useState(null); // valoarea efectivă fără override
  const [lockedColors, setLockedColors] = useState([]); // [{color, locked_by_label, released_at}]

  // Fetch culorile de scule deja alocate altor șantiere care nu s-au încheiat
  useEffect(() => {
    (async () => {
      try {
        const df = entry.start_date;
        const dt = entry.end_date || entry.start_date;
        if (!df || !dt) return;
        const params = new URLSearchParams({ date_from: df, date_to: dt });
        if (entry.project_id) params.set("exclude_project_id", entry.project_id);
        if (entry.regie_id) params.set("exclude_regie_id", entry.regie_id);
        const { data } = await api.get(`/tool-sets/availability?${params.toString()}`);
        setLockedColors(data?.locked || []);
      } catch { setLockedColors([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.start_date, entry.end_date, entry.project_id, entry.regie_id]);

  // Inline edit pentru perioada (start_date / end_date) — pentru proiecte și regii
  const [editPeriod, setEditPeriod] = useState(false);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [periodIndeterminate, setPeriodIndeterminate] = useState(false);
  const [savingPeriod, setSavingPeriod] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!clickedDay || (!entry.regie_id && !entry.project_id)) {
      setDayMinPeople("");
      setDayOverrideExists(false);
      setDayDefault(null);
      return;
    }
    const url = entry.regie_id
      ? `/regii/${entry.regie_id}/days/${clickedDay}/effective`
      : `/projects/${entry.project_id}/days/${clickedDay}/effective`;
    api.get(url).then((r) => {
      if (cancelled) return;
      const d = r.data || {};
      setDayDefault(d.default_min_people != null ? d.default_min_people : null);
      if (d.has_override) {
        setDayMinPeople(String(d.min_people ?? ""));
        setDayOverrideExists(true);
      } else {
        setDayMinPeople("");
        setDayOverrideExists(false);
      }
    }).catch(() => {
      // fallback: deduce default din regie sau project (cu existingWeek peste)
      let def = 0;
      if (entry.regie_id && regie) {
        def = existingWeek?.min_people != null ? existingWeek.min_people : (regie.default_min_people || 0);
      } else if (entry.project_id && project) {
        def = project.min_people || 0;
      }
      setDayDefault(def);
      setDayMinPeople("");
      setDayOverrideExists(false);
    });
    return () => { cancelled = true; };
  }, [clickedDay, entry.regie_id, entry.project_id, regie, project, existingWeek]);

  const saveDayOverride = async () => {
    if (!clickedDay) return;
    const url = entry.regie_id
      ? `/regii/${entry.regie_id}/days/${clickedDay}`
      : `/projects/${entry.project_id}/days/${clickedDay}`;
    const raw = String(dayMinPeople).trim();
    let body;
    if (raw === "") {
      body = { min_people: null };
    } else {
      const n = parseInt(raw);
      if (isNaN(n) || n < 0) { toast.error("Număr invalid"); return; }
      body = { min_people: n };
    }
    setSavingDay(true);
    try {
      await api.put(url, body);
      toast.success(raw === "" ? "Override pe zi eliminat" : `Necesar pe ${clickedDay}: ${body.min_people} pers`);
      setDayOverrideExists(raw !== "");
      onSaved && onSaved({ silent: true });
    } catch (e) {
      toast.error("Eroare la salvare");
    } finally { setSavingDay(false); }
  };

  const clearDayOverride = async () => {
    setDayMinPeople("");
    await saveDayOverride();
  };

  // Fetch project details if linked
  useEffect(() => {
    if (entry.project_id) {
      api.get(`/projects/${entry.project_id}`).then((r) => {
        setProject(r.data);
        setPeriodStart(r.data?.start_date || "");
        setPeriodEnd(r.data?.end_date || "");
        setPeriodIndeterminate(!!r.data?.indeterminate_end);
      }).catch(() => {});
    } else if (entry.regie_id) {
      api.get(`/regii/${entry.regie_id}`).then((r) => {
        setRegie(r.data);
        setPeriodStart(r.data?.start_date || "");
        setPeriodEnd(r.data?.end_date || "");
        setPeriodIndeterminate(!!r.data?.indeterminate_end);
      }).catch(() => {});
    }
  }, [entry.project_id, entry.regie_id]);

  const savePeriod = async () => {
    const isProj = !!project;
    const item = project || regie;
    if (!item) return;
    if (!periodIndeterminate && periodStart && periodEnd && periodEnd < periodStart) {
      toast.error("Data sfârșit nu poate fi înainte de început");
      return;
    }
    setSavingPeriod(true);
    try {
      const url = isProj ? `/projects/${item.id}` : `/regii/${item.id}`;
      // Preserve full payload to avoid wiping fields (la fel ca în QuickEditPopover)
      const payload = isProj
        ? { ...item, start_date: periodStart || null, end_date: periodEnd || null }
        : { ...item, start_date: periodStart || null, end_date: periodIndeterminate ? null : (periodEnd || null), indeterminate_end: !!periodIndeterminate };
      const r = await api.put(url, payload);
      if (isProj) setProject(r.data || { ...item, ...payload });
      else setRegie(r.data || { ...item, ...payload });
      toast.success("Perioadă actualizată");
      setEditPeriod(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare salvare perioadă");
    } finally { setSavingPeriod(false); }
  };

  const [pendingConflict, setPendingConflict] = useState(null); // { person, otherAssignments }

  const togglePerson = (id) => {
    if (selectedPersons.includes(id)) {
      setSelectedPersons((s) => s.filter((x) => x !== id));
      return;
    }
    // Adăugare: dacă persoana are alocări care se suprapun cu entry-ul curent → întreabă utilizatorul
    const person = (persons || []).find((p) => p.id === id);
    const otherAssignments = conflictFor(person?.assignments).filter((a) => {
      // Considerăm conflict doar dacă datele se suprapun cu entry-ul curent
      const aStart = a.start_date || "";
      const aEnd = a.end_date || aStart;
      const eStart = entry.start_date || "";
      const eEnd = entry.end_date || eStart;
      return aStart <= eEnd && aEnd >= eStart;
    });
    if (otherAssignments.length > 0) {
      setPendingConflict({ person, otherAssignments });
      return;
    }
    setSelectedPersons((s) => [...s, id]);
  };

  const acceptConflictBoth = () => {
    if (pendingConflict) setSelectedPersons((s) => [...s, pendingConflict.person.id]);
    setPendingConflict(null);
  };
  const acceptConflictMove = async () => {
    if (!pendingConflict) return;
    const { person, otherAssignments } = pendingConflict;
    try {
      // Scoatem persoana din alocările vechi (care se suprapun)
      for (const a of otherAssignments) {
        await api.post(`/schedule/${a.schedule_id}/remove-person`, { person_id: person.id });
      }
      setSelectedPersons((s) => [...s, person.id]);
      toast.success(`${person.name} a fost mutat(ă) din ${otherAssignments.length} alocare${otherAssignments.length === 1 ? "" : "i"} anterioară.`);
      // Reîncărcăm datele pentru a vedea că persoana e gata să fie alocată (părintele se actualizează)
      if (typeof onSaved === "function") onSaved({ silent: true });
    } catch (e) {
      toast.error("Eroare la mutarea persoanei. Persoana va fi adăugată la alocarea curentă fără a fi scoasă din celelalte.");
      setSelectedPersons((s) => [...s, person.id]);
    } finally {
      setPendingConflict(null);
    }
  };
  const toggleCar = (id) => setSelectedCars((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const conflictFor = (assignments) => (assignments || []).filter((a) => a.schedule_id !== entry.id);

  // Determină dacă persoana are concediu/indisponibilitate care se suprapune cu zilele entry-ului.
  // Dacă există _clickedDay, verifică doar acea zi. Altfel verifică tot intervalul start_date..end_date.
  const personLeaveOverlap = (p) => {
    const vacs = p?.vacations || [];
    if (vacs.length === 0) return null;
    const rangeStart = clickedDay || entry.start_date || "";
    const rangeEnd = clickedDay || entry.end_date || rangeStart;
    if (!rangeStart || !rangeEnd) return null;
    return vacs.find((v) => (v.start_date || "") <= rangeEnd && rangeStart <= (v.end_date || "")) || null;
  };

  const filteredPersons = (persons || []).filter((p) => !qp || p.name.toLowerCase().includes(qp.toLowerCase()));
  const filteredCars = (cars || []).filter((c) => !qc || c.label.toLowerCase().includes(qc.toLowerCase()));
  // Prioritate vizuală: șoferi (cu permis) primii, apoi EKAS, apoi restul.
  // Asta se aplică atât pe persoanele alocate, cât și pe cele disponibile, ca să
  // sară în ochi cei utili pentru proiect.
  const personSortPriority = (p) => {
    if (p.permis_conducere) return 0;       // șoferi primii
    if (p.ekas) return 1;                   // EKAS după
    return 2;                                // restul
  };
  const sortByRole = (arr) => [...arr].sort((a, b) => {
    const da = personSortPriority(a) - personSortPriority(b);
    if (da !== 0) return da;
    return (a.name || "").localeCompare(b.name || "", "ro");
  });
  const assignedPersons = sortByRole(filteredPersons.filter((p) => selectedPersons.includes(p.id)));
  const otherPersonsAll = filteredPersons.filter((p) => !selectedPersons.includes(p.id));
  // Separă persoanele neselectate care sunt în concediu / indisponibile pe zilele entry-ului
  const otherPersons = sortByRole(otherPersonsAll.filter((p) => !personLeaveOverlap(p)));
  const onLeavePersons = sortByRole(otherPersonsAll.filter((p) => personLeaveOverlap(p)));
  const assignedCars = filteredCars.filter((c) => selectedCars.includes(c.id));
  const otherCars = filteredCars.filter((c) => !selectedCars.includes(c.id));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/schedule/${entry.id}`, {
        project_id: entry.project_id,
        regie_id: entry.regie_id || null,
        label: entry.label,
        is_regie: entry.is_regie || false,
        start_date: entry.start_date,
        end_date: entry.end_date,
        color: entry.color,
        allocation_color: allocColor || null,
        note: note || null,
        person_ids: selectedPersons,
        car_ids: selectedCars,
      });
      // Entry-urile duplicate agregate în același card → golește-le, ca să nu reapară alocări-fantomă
      const otherIds = (entry._merged_ids || []).filter((id) => id !== entry.id);
      if (otherIds.length > 0) {
        await api.post("/schedule/clear-allocations", { ids: otherIds }).catch(() => {});
      }
      toast.success("Alocări salvate");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  const saveWeekOverride = async () => {
    if (!isRegieEntry || !weekStart) return;
    setSavingWeek(true);
    try {
      const minP = weekMinPeople === "" ? null : parseInt(weekMinPeople, 10);
      await api.put(`/regii/${entry.regie_id}/weeks`, {
        week_start: weekStart,
        people_count: minP ?? 0,
        min_people: minP,
        max_people: minP,
      });
      toast.success(`Necesar săptămână salvat: ${minP ?? "—"} persoane`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare salvare necesar săptămână");
    } finally { setSavingWeek(false); }
  };

  const clearWeekOverride = async () => {
    if (!isRegieEntry || !weekStart) return;
    setSavingWeek(true);
    try {
      await api.delete(`/regii/${entry.regie_id}/weeks?week_start=${weekStart}`);
      toast.success("Override săptămână șters — se folosesc valorile default");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare ștergere override");
    } finally { setSavingWeek(false); }
  };

  const [deletingSource, setDeletingSource] = useState(false);
  const deleteSource = async () => {
    // Șterge proiectul sau regia legată — toate alocările (schedule_entries) cad cascadat
    const isRegie = entry.is_regie && entry.regie_id;
    const isProject = !!entry.project_id && !isRegie;
    if (!isRegie && !isProject) {
      toast.error("Nu se poate șterge: intrare orfană");
      return;
    }
    const what = isRegie ? "regia" : "proiectul";
    const ok = await confirm({
      title: `Șterge ${what}?`,
      body: `Vei șterge DEFINITIV ${what} „${entry.label}" împreună cu TOATE alocările din planificator.\n\nAceastă acțiune NU poate fi anulată. Continui?`,
      confirmText: "DA, șterge definitiv",
      cancelText: "Anulează",
      danger: true,
    });
    if (!ok) return;
    setDeletingSource(true);
    try {
      if (isRegie) {
        await api.delete(`/regii/${entry.regie_id}`);
        toast.success(`Regia „${entry.label}" a fost ștearsă`);
      } else {
        await api.delete(`/projects/${entry.project_id}`);
        toast.success(`Proiectul „${entry.label}" a fost șters`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare ștergere");
    } finally { setDeletingSource(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          const t = e.target;
          if (!t) return;
          const closest = (sel) => (t.nodeType === 1 ? t.closest(sel) : null);
          if (
            closest('[data-radix-popper-content-wrapper]') ||
            closest('[data-radix-popover-content]') ||
            closest('[data-radix-select-content]') ||
            closest('[data-sonner-toast]')
          ) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: entry.color || "#3B82F6" }} />
            <span className="truncate">{entry.label}</span>
          </DialogTitle>
          {(project?.adresa || regie?.adresa) && (
            <div
              className="flex items-start gap-1.5 text-[12px] text-zinc-600 mt-1 leading-snug"
              data-testid="assign-dialog-address"
            >
              <span className="text-base shrink-0 mt-px">📍</span>
              <span className="break-words">{project?.adresa || regie?.adresa}</span>
            </div>
          )}
        </DialogHeader>
        <div className="flex items-center gap-3 text-xs text-zinc-500 border-b border-[var(--border)] pb-2 mb-3">
          <CalendarDays size={14}/>
          {(project || regie) ? (
            <button
              type="button"
              onClick={() => {
                const item = project || regie;
                if (!item) return;
                setPeriodStart(item.start_date || "");
                setPeriodEnd(item.end_date || "");
                setPeriodIndeterminate(!!item.indeterminate_end);
                setEditPeriod(true);
                // Scroll smooth la secțiunea Detalii
                setTimeout(() => {
                  document.querySelector('[data-testid="project-details-pane"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 80);
              }}
              className="inline-flex items-center gap-1.5 group hover:text-[var(--primary)] cursor-pointer"
              title="Click pentru a modifica perioada proiectului"
              data-testid="header-edit-period-shortcut"
            >
              <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">
                {fmtRoDate(entry.start_date)} → {fmtRoDate(entry.end_date)}
              </span>
              <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity text-amber-600 font-semibold">✎ Modifică</span>
            </button>
          ) : (
            <span>{fmtRoDate(entry.start_date)} → {fmtRoDate(entry.end_date)}</span>
          )}
          {entry.is_regie && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] uppercase tracking-wider font-semibold">regie</span>}
        </div>

        {/* Selector culoare set scule (atribuire vizuală — coincide cu setul de scule din inventar) */}
        <div className="mb-3 p-2.5 rounded-md border border-zinc-200 bg-white" data-testid="assign-color-block">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-600 mb-1.5">
            🎨 Set scule atribuit (opțional) — coincide cu culoarea sculelor din Inventar
          </div>
          {lockedColors.length > 0 && (
            <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1.5">
              🔒 <strong>{lockedColors.length}</strong> {lockedColors.length === 1 ? "set este blocat" : "seturi sunt blocate"} la alte șantiere în acest interval.
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setAllocColor("")}
              className={`text-[11px] px-2 py-1 rounded border-2 ${!allocColor ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"}`}
              data-testid="assign-color-none"
            >
              ⬜ Fără
            </button>
            {INVENTORY_SET_COLORS.map((c) => {
              const lock = lockedColors.find((l) => l.color === c.code);
              const isLocked = !!lock;
              const isMine = allocColor === c.code;
              // Permitem tot deselectarea culorii proprii; dar dacă e lock-uit la altcineva, blocăm click-ul
              const disabled = isLocked && !isMine;
              const relDate = lock?.released_at && lock.released_at !== "9999-12-31" ? lock.released_at : "nedefinit";
              return (
                <button
                  key={c.code}
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && setAllocColor(c.code)}
                  className={`text-[11px] px-2 py-1 rounded border-2 inline-flex items-center gap-1 transition-all ${
                    disabled
                      ? "opacity-40 cursor-not-allowed border-zinc-200 bg-zinc-100"
                      : isMine
                        ? "scale-110 ring-2 ring-offset-1"
                        : "border-zinc-200 hover:scale-105"
                  }`}
                  style={isMine ? { borderColor: c.hex, background: c.hex + "22" } : {}}
                  data-testid={`assign-color-${c.code}${disabled ? "-locked" : ""}`}
                  title={disabled ? `🔒 Deja folosit la „${lock.locked_by_label}" până la ${relDate}` : `Set ${c.label}`}
                >
                  {c.emoji} {c.label}{disabled ? " 🔒" : ""}
                </button>
              );
            })}
          </div>
          {lockedColors.length > 0 && (
            <div className="mt-1.5 space-y-0.5">
              {lockedColors.map((l) => (
                <div key={l.color} className="text-[10px] text-zinc-600" data-testid={`locked-info-${l.color}`}>
                  <span className="font-semibold">
                    {(INVENTORY_SET_COLORS.find((c) => c.code === l.color)?.emoji) || "•"}
                    {" "}{(INVENTORY_SET_COLORS.find((c) => c.code === l.color)?.label) || l.color}
                  </span>
                  <span className="opacity-70">
                    {" "}→ „{l.locked_by_label}" până la {l.released_at === "9999-12-31" ? "final regie (indeterminat)" : (l.released_at || "nedefinit")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {isRegieEntry && weekStart && (
          <div className="mb-3 p-3 rounded-md bg-amber-50 border border-amber-300" data-testid="regie-week-override">
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-2 flex items-center gap-1.5">
              📅 Necesar pentru săptămâna {weekStart}
              {existingWeek && <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[9px]">override activ</span>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-amber-900 font-medium">Min persoane</label>
                <Input
                  type="number"
                  min={0}
                  placeholder="default"
                  value={weekMinPeople}
                  onChange={(e) => setWeekMinPeople(e.target.value)}
                  className="bg-white"
                  data-testid="week-min-people"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2.5">
              <Button
                size="sm"
                onClick={saveWeekOverride}
                disabled={savingWeek}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                data-testid="save-week-override"
              >
                {savingWeek ? "Se salvează…" : "💾 Salvează doar pentru săptămâna asta"}
              </Button>
              {existingWeek && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearWeekOverride}
                  disabled={savingWeek}
                  className="text-xs border-amber-400 text-amber-800 hover:bg-amber-100"
                  data-testid="clear-week-override"
                >
                  Șterge override
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Per-DAY override (regii ȘI proiecte) */}
        {clickedDay && (entry.regie_id || entry.project_id) && (
          <div className="mb-3 p-3 rounded-md bg-purple-50 border border-purple-300" data-testid="day-override">
            <div className="text-[10px] uppercase tracking-wider font-bold text-purple-800 mb-2 flex items-center gap-1.5">
              📆 Necesar DOAR pentru ziua {clickedDay}
              {dayOverrideExists && <span className="px-1.5 py-0.5 rounded bg-purple-200 text-purple-900 text-[9px]">override pe zi activ</span>}
            </div>
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-end">
              <div>
                <label className="text-[11px] text-purple-900 font-medium">Min persoane (gol = revine la default)</label>
                <Input
                  type="number"
                  min={0}
                  placeholder={dayDefault != null ? `default: ${dayDefault}` : "default"}
                  value={dayMinPeople}
                  onChange={(e) => setDayMinPeople(e.target.value)}
                  className="bg-white"
                  data-testid="day-min-people"
                />
              </div>
              <Button
                size="sm"
                onClick={saveDayOverride}
                disabled={savingDay}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                data-testid="save-day-override"
              >
                {savingDay ? "Salvez…" : "💾 Doar pe această zi"}
              </Button>
              {dayOverrideExists && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearDayOverride}
                  disabled={savingDay}
                  className="text-xs border-purple-400 text-purple-800 hover:bg-purple-100"
                  data-testid="clear-day-override"
                >
                  ✗ Șterge
                </Button>
              )}
            </div>
            <div className="text-[10px] text-purple-700 mt-1 italic">
              Override-ul pe zi are prioritate față de săptămână și default. Sincronizat cu Management.
            </div>
          </div>
        )}

        {/* Detalii complete proiect / regie */}
        {(project || regie) && (() => {
          const item = project || regie;
          const isProj = !!project;
          return (
            <div className="mb-3 p-3 rounded-md bg-[var(--primary-subtle)] border border-[var(--primary)]/20" data-testid="project-details-pane">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--primary)]">
                  📋 Detalii {isProj ? "proiect" : "regie"}
                </div>
                {isProj && (
                  <a
                    href={`/proiecte?project=${item.id}&edit=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-[var(--primary)] hover:underline font-semibold"
                    data-testid="open-project-detail-link"
                  >
                    Deschide pagina completă →
                  </a>
                )}
                {!isProj && regie && (
                  <a
                    href={`/proiecte?regie=${item.id}&edit=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-[var(--primary)] hover:underline font-semibold"
                    data-testid="open-regie-detail-link"
                  >
                    Deschide pagina completă →
                  </a>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-zinc-700">
                {item.adresa && <div className="col-span-2">📍 <strong>Adresă:</strong> {item.adresa}</div>}
                {item.auftraggeber && <div>🏢 <strong>Auftraggeber:</strong> {item.auftraggeber}</div>}
                {item.auftraggeber_phone && <div>📞 {item.auftraggeber_phone}</div>}
                {item.auftraggeber_contact && <div className="col-span-2 text-[11px] text-zinc-500">Contact: {item.auftraggeber_contact}</div>}
                {item.bauherr && <div>👤 <strong>Bauherr:</strong> {item.bauherr}</div>}
                {item.bauherr_phone && <div>📞 {item.bauherr_phone}</div>}
                {item.bauleiter && <div>👷 <strong>Bauleiter:</strong> {item.bauleiter}</div>}
                {item.bauleiter_phone && <div>📞 {item.bauleiter_phone}</div>}
                {item.ofertant && <div>📤 <strong>Ofertant:</strong> {item.ofertant}</div>}
                {item.firma && <div>🏷 <strong>Firma:</strong> {item.firma}</div>}
                {item.category && <div>📂 <strong>Categorie:</strong> {item.category}{item.status ? ` · ${item.status}` : ""}</div>}
                {(item.start_date || item.end_date || item.indeterminate_start || editPeriod) && (
                  <div className="col-span-2 flex items-start gap-2">
                    <span className="shrink-0">📅 <strong>Perioadă:</strong></span>
                    {!editPeriod ? (
                      <>
                        <span className="flex-1">
                          {item.indeterminate_start ? "♾ TBD" : (item.start_date || "—")} → {item.indeterminate_end ? "♾ nedeterminată" : (item.end_date || "—")}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditPeriod(true)}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100 text-[10px] font-semibold"
                          data-testid="edit-period-btn"
                          title="Modifică perioada"
                        >
                          ✎ Modifică
                        </button>
                      </>
                    ) : (
                      <div className="flex-1 flex items-center gap-2 flex-wrap bg-amber-50 border border-amber-300 rounded p-1.5">
                        <Input
                          type="date"
                          value={periodStart}
                          onChange={(e) => setPeriodStart(e.target.value)}
                          className="h-7 text-xs w-auto"
                          data-testid="edit-period-start"
                        />
                        <span>→</span>
                        <Input
                          type="date"
                          value={periodEnd}
                          disabled={!!periodIndeterminate}
                          onChange={(e) => setPeriodEnd(e.target.value)}
                          className="h-7 text-xs w-auto"
                          data-testid="edit-period-end"
                        />
                        {!isProj && (
                          <label className="flex items-center gap-1 text-[10px] text-zinc-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!periodIndeterminate}
                              onChange={(e) => setPeriodIndeterminate(e.target.checked)}
                            />
                            ♾ Nedeterminată
                          </label>
                        )}
                        <Button
                          size="sm"
                          onClick={savePeriod}
                          disabled={savingPeriod}
                          className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="save-period-btn"
                        >
                          {savingPeriod ? "Salvez…" : "💾 Salvează"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditPeriod(false);
                            setPeriodStart(item.start_date || "");
                            setPeriodEnd(item.end_date || "");
                            setPeriodIndeterminate(!!item.indeterminate_end);
                          }}
                          className="h-7 text-xs"
                          data-testid="cancel-period-btn"
                        >
                          Anulează
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {(item.min_people > 0 || item.max_people > 0 || item.default_min_people > 0) && (
                  <div>👥 <strong>Necesar persoane:</strong> {item.min_people || item.default_min_people || 0}{item.max_people && item.max_people !== item.min_people ? `–${item.max_people}` : ""}</div>
                )}
                {(item.min_cars > 0 || item.default_min_cars > 0) && (
                  <div>🚗 <strong>Necesar mașini:</strong> {item.min_cars || item.default_min_cars || 0}</div>
                )}
              </div>
              {item.description && (
                <div className="text-xs text-zinc-700 mt-2 pt-2 border-t border-[var(--primary)]/10">
                  <strong>Descriere:</strong> {item.description}
                </div>
              )}
              {item.note && (
                <div className="text-xs text-zinc-700 mt-1.5"><strong>Notă:</strong> {item.note}</div>
              )}
              {Array.isArray(item.zones) && item.zones.length > 0 && (
                <div className="text-xs text-zinc-700 mt-1.5">
                  <strong>Zone:</strong>
                  <span className="ml-2 inline-flex flex-wrap gap-1">
                    {item.zones.map((z, i) => (
                      <span key={`zone-${z.type || "x"}-${z.count || 0}-${i}`} className="px-1.5 py-0.5 rounded bg-white border border-[var(--primary)]/30 text-[11px]">
                        {(z.type || z.label || "Altele")}{z.count > 1 ? ` (×${z.count} poz.)` : ""}{z.description ? ` — ${z.description}` : ""}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {item.work_zones && (
                <div className="text-xs text-zinc-700 mt-1.5"><strong>Lucru de făcut:</strong> {item.work_zones}</div>
              )}
              {item.special && (
                <div className="text-xs text-zinc-700 mt-1.5"><strong>Mențiuni speciale:</strong> {item.special}</div>
              )}
            </div>
          );
        })()}
        <Tabs defaultValue="persoane">
          <TabsList>
            <TabsTrigger value="persoane"><Users size={14} className="mr-1.5"/> Persoane ({selectedPersons.length})</TabsTrigger>
            <TabsTrigger value="autoturisme"><CarIcon size={14} className="mr-1.5"/> Autoturisme ({selectedCars.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="persoane">
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400"/>
              <Input className="pl-8" placeholder="Caută persoană..." value={qp} onChange={(e) => setQp(e.target.value)} data-testid="search-persons"/>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {(() => {
                // Warning EKAS: proiectul are zone EKAS (sau coduri 6) și niciun EKAS alocat
                const proj = project;
                if (!proj) return null;
                const zones = proj.zones || [];
                const hasEkasZone = zones.some((z) => (z.type || z.code || "").toString().toUpperCase().includes("EKAS"));
                // Heuristic adițional: categoria proiectului 6.* / 7.* (lucrări specializate) — opțional, doar dacă zones e gol
                if (!hasEkasZone) return null;
                const ekasAssigned = assignedPersons.filter((p) => p.ekas).length;
                if (ekasAssigned > 0) return null; // are deja
                return (
                  <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-2.5 shadow-sm" data-testid="ekas-warning">
                    <div className="flex items-start gap-2 text-xs text-amber-900">
                      <span className="text-base leading-none">⚠️</span>
                      <div className="flex-1">
                        <div className="font-bold flex items-center gap-1.5">Atenție — proiect cu zone <span className="text-yellow-500">⭐</span> EKAS</div>
                        <div className="text-[11px] mt-0.5">Niciuna din persoanele alocate nu are certificare EKAS. Selectează cel puțin un EKAS din lista de mai jos (afișați primii după șoferi).</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {assignedPersons.length > 0 && (
                <div className="rounded-lg border-2 border-green-500 bg-green-50 p-2 shadow-sm" data-testid="assigned-persons-box">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-green-700 mb-2 px-1">
                    <CheckCircle2 size={14}/> Deja alocați ({assignedPersons.length})
                  </div>
                  <div className="space-y-1">
                    {assignedPersons.map((p) => {
                      const lv = personLeaveOverlap(p);
                      const isUnavail = lv?.kind === "unavailable";
                      const leaveLabel = isUnavail ? "Indisponibil" : "Concediu";
                      const leaveIcon = isUnavail ? "🚫" : "🏖";
                      const adminLabel = p.special_label || (p.is_admin_user ? "Admin" : null);
                      return (
                        <label
                          key={p.id}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-sm cursor-pointer ${lv ? (isUnavail ? "border-amber-400 bg-amber-50 hover:border-amber-600" : "border-blue-400 bg-blue-50 hover:border-blue-600") : "border-green-300 bg-white hover:border-green-500"}`}
                          data-testid={`pick-person-${p.id}`}
                        >
                          <Checkbox
                            checked
                            onCheckedChange={() => togglePerson(p.id)}
                            className="data-[state=checked]:!bg-white data-[state=checked]:!text-red-600 data-[state=checked]:border-red-600"
                          />
                          <span className={`flex-1 font-semibold ${lv ? "text-zinc-700 line-through decoration-1" : "text-green-900"}`}>
                            {p.name}
                            {p.ekas && <span className="ml-1 text-yellow-500" title="EKAS" data-testid={`ekas-star-assigned-${p.id}`}>⭐</span>}
                            {p.permis_conducere && <span className="ml-1 text-blue-600" title="Permis de conducere" data-testid={`license-icon-assigned-${p.id}`}>🚗</span>}
                          </span>
                          {adminLabel && (
                            <span
                              className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 bg-purple-200 text-purple-900 border border-purple-300"
                              title="Utilizator admin"
                              data-testid={`assigned-person-admin-${p.id}`}
                            >
                              <span>👑</span>
                              <span>{adminLabel}</span>
                            </span>
                          )}
                          {lv && (
                            <span
                              className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${isUnavail ? "bg-amber-200 text-amber-900" : "bg-blue-200 text-blue-900"}`}
                              title={`${leaveLabel}: ${fmtRoDate(lv.start_date)} → ${fmtRoDate(lv.end_date)}${lv.note ? " · " + lv.note : ""}`}
                              data-testid={`assigned-person-leave-${p.id}`}
                            >
                              <span>{leaveIcon}</span>
                              <span>{leaveLabel}</span>
                            </span>
                          )}
                          {user?.role === "admin" && (
                            <button
                              type="button"
                              onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); window.open(`/personal/${p.id}`, "_blank", "noopener,noreferrer"); }}
                              className="ml-1 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded p-0.5 transition-colors"
                              title="Deschide fișa persoanei — editează orele lucrate în tab-ul Calendar"
                              data-testid={`edit-hours-shortcut-${p.id}`}
                            >
                              <Pencil size={12}/>
                            </button>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="space-y-1">
                {otherPersons.map((p) => {
                  const otherAsgn = conflictFor(p.assignments);
                  const conflict = otherAsgn.length > 0;
                  const adminLabel = p.special_label || (p.is_admin_user ? "Admin" : null);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-sm cursor-pointer ${conflict ? "border-red-200 bg-red-50" : adminLabel ? "border-purple-200 bg-purple-50/30 hover:bg-purple-50" : "border-zinc-200 hover:bg-zinc-50"}`}
                      data-testid={`pick-person-${p.id}`}
                    >
                      <Checkbox
                        checked={false}
                        onCheckedChange={() => togglePerson(p.id)}
                        className="data-[state=checked]:!bg-white data-[state=checked]:!text-red-600 data-[state=checked]:border-red-600"
                      />
                      <span className="flex-1 font-medium">
                        {p.name}
                        {p.ekas && <span className="ml-1 text-yellow-500" title="EKAS" data-testid={`ekas-star-${p.id}`}>⭐</span>}
                        {p.permis_conducere && <span className="ml-1 text-blue-600" title="Permis de conducere" data-testid={`license-icon-${p.id}`}>🚗</span>}
                      </span>
                      {adminLabel && (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 bg-purple-200 text-purple-900 border border-purple-300"
                          title="Utilizator admin"
                          data-testid={`person-admin-${p.id}`}
                        >
                          <span>👑</span>
                          <span>{adminLabel}</span>
                        </span>
                      )}
                      {conflict && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-100 px-1.5 py-0.5 rounded" title={otherAsgn.map((a) => a.label).join(", ")}>
                          <AlertTriangle size={10}/> alocat la {otherAsgn[0].label}{otherAsgn.length > 1 ? ` +${otherAsgn.length - 1}` : ""}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {onLeavePersons.length > 0 && (
                <div className="rounded-lg border-2 border-amber-300 bg-amber-50/40 p-2" data-testid="onleave-persons-box">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800 mb-2 px-1">
                    <AlertTriangle size={14}/> În concediu / indisponibili ({onLeavePersons.length})
                    <span className="text-[10px] font-normal text-amber-700 normal-case">— bifați doar dacă chiar e necesar</span>
                  </div>
                  <div className="space-y-1">
                    {onLeavePersons.map((p) => {
                      const lv = personLeaveOverlap(p);
                      const isUnavail = lv?.kind === "unavailable";
                      const leaveLabel = isUnavail ? "Indisponibil" : "Concediu";
                      const leaveIcon = isUnavail ? "🚫" : "🏖";
                      const otherAsgn = conflictFor(p.assignments);
                      const conflict = otherAsgn.length > 0;
                      return (
                        <label
                          key={p.id}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-sm cursor-pointer ${isUnavail ? "border-amber-300 bg-amber-50 hover:border-amber-500" : "border-blue-300 bg-blue-50 hover:border-blue-500"}`}
                          data-testid={`pick-person-${p.id}`}
                        >
                          <Checkbox
                            checked={false}
                            onCheckedChange={() => togglePerson(p.id)}
                            className="data-[state=checked]:!bg-white data-[state=checked]:!text-red-600 data-[state=checked]:border-red-600"
                          />
                          <span className="flex-1 font-medium text-zinc-700 line-through decoration-1">
                            {p.name}
                            {p.ekas && <span className="ml-1 text-yellow-500 no-underline" title="EKAS">⭐</span>}
                            {p.permis_conducere && <span className="ml-1 text-blue-600 no-underline" title="Permis de conducere">🚗</span>}
                          </span>
                          {(p.special_label || p.is_admin_user) && (
                            <span
                              className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 bg-purple-200 text-purple-900 border border-purple-300"
                              title="Utilizator admin"
                              data-testid={`onleave-person-admin-${p.id}`}
                            >
                              <span>👑</span>
                              <span>{p.special_label || "Admin"}</span>
                            </span>
                          )}
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${isUnavail ? "bg-amber-200 text-amber-900" : "bg-blue-200 text-blue-900"}`}
                            title={`${leaveLabel}: ${fmtRoDate(lv?.start_date)} → ${fmtRoDate(lv?.end_date)}${lv?.note ? " · " + lv.note : ""}`}
                            data-testid={`person-leave-${p.id}`}
                          >
                            <span>{leaveIcon}</span>
                            <span>{leaveLabel}</span>
                          </span>
                          {conflict && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-100 px-1.5 py-0.5 rounded" title={otherAsgn.map((a) => a.label).join(", ")}>
                              <AlertTriangle size={10}/> alocat
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {filteredPersons.length === 0 && <div className="text-xs text-zinc-400 italic py-4 text-center">Niciun rezultat.</div>}
            </div>
          </TabsContent>
          <TabsContent value="autoturisme">
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400"/>
              <Input className="pl-8" placeholder="Caută autoturism..." value={qc} onChange={(e) => setQc(e.target.value)} data-testid="search-cars"/>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {assignedCars.length > 0 && (
                <div className="rounded-lg border-2 border-green-500 bg-green-50 p-2 shadow-sm" data-testid="assigned-cars-box">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-green-700 mb-2 px-1">
                    <CheckCircle2 size={14}/> Deja alocate ({assignedCars.length})
                  </div>
                  <div className="space-y-1">
                    {assignedCars.map((c) => (
                      <div key={c.id} className="rounded border-2 border-green-300 bg-white" data-testid={`pick-car-${c.id}`}>
                        <label className="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:border-green-500">
                          <Checkbox
                            checked
                            onCheckedChange={() => toggleCar(c.id)}
                            className="data-[state=checked]:!bg-white data-[state=checked]:!text-red-600 data-[state=checked]:border-red-600"
                          />
                          <span className="flex-1 font-semibold text-green-900">{c.label}</span>
                          {c.special_notes && (
                            <span className="text-[10px] text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded font-bold" title={c.special_notes} data-testid={`car-${c.id}-has-notes`}>
                              ⚠ Note
                            </span>
                          )}
                          {c.status === "defect" && (
                            c.defect_drivable
                              ? <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title={c.defect_reason || "Defect — poate fi condusă"}>defect (rulează)</span>
                              : <span className="text-[10px] text-red-600 bg-red-100 px-1.5 py-0.5 rounded" title={c.defect_reason || "Defect — imobilizată"}>defect</span>
                          )}
                        </label>
                        {c.special_notes && (
                          <div className="px-2.5 pb-1.5 -mt-0.5">
                            <div className="text-[11px] text-amber-900 bg-amber-50 border-l-2 border-amber-400 rounded-r px-2 py-1 whitespace-pre-line" data-testid={`car-${c.id}-notes-text`}>
                              {c.special_notes}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1">
                {otherCars.map((c) => {
                  const otherAsgn = conflictFor(c.assignments);
                  const conflict = otherAsgn.length > 0;
                  return (
                    <div
                      key={c.id}
                      className={`rounded border ${conflict ? "border-red-200 bg-red-50" : "border-zinc-200 hover:bg-zinc-50"}`}
                      data-testid={`pick-car-${c.id}`}
                    >
                      <label className="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer">
                        <Checkbox
                          checked={false}
                          onCheckedChange={() => toggleCar(c.id)}
                          className="data-[state=checked]:!bg-white data-[state=checked]:!text-red-600 data-[state=checked]:border-red-600"
                        />
                        <span className="flex-1 font-medium">{c.label}</span>
                        {c.special_notes && (
                          <span className="text-[10px] text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded font-bold" title={c.special_notes} data-testid={`car-${c.id}-has-notes`}>
                            ⚠ Note
                          </span>
                        )}
                        {c.status === "defect" && (
                          c.defect_drivable
                            ? <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title={c.defect_reason || "Defect — poate fi condusă"}>defect (rulează)</span>
                            : <span className="text-[10px] text-red-600 bg-red-100 px-1.5 py-0.5 rounded" title={c.defect_reason || "Defect — imobilizată"}>defect</span>
                        )}
                        {conflict && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-100 px-1.5 py-0.5 rounded" title={otherAsgn.map((a) => a.label).join(", ")}>
                            <AlertTriangle size={10}/> alocat la {otherAsgn[0].label}{otherAsgn.length > 1 ? ` +${otherAsgn.length - 1}` : ""}
                          </span>
                        )}
                      </label>
                      {c.special_notes && (
                        <div className="px-2.5 pb-1.5 -mt-0.5">
                          <div className="text-[11px] text-amber-900 bg-amber-50 border-l-2 border-amber-400 rounded-r px-2 py-1 whitespace-pre-line" data-testid={`car-${c.id}-notes-text`}>
                            {c.special_notes}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {filteredCars.length === 0 && <div className="text-xs text-zinc-400 italic py-4 text-center">Niciun rezultat.</div>}
            </div>
          </TabsContent>
        </Tabs>

        {/* Notă pentru imaginea distribuită — apare sub regie/proiect pe card și în export */}
        <div className="mt-3 px-1">
          <Label className="small-label">📝 Notă pentru echipă (apare pe imaginea distribuită)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="ex: Gheorghe merge cu echipa Speztec până la magazie · Întâlnire la 7:30 la depou"
            className="mt-1 min-h-[50px] text-sm"
            data-testid="entry-note"
          />
        </div>

        <DialogFooter>
          {canDeleteSource && (entry.project_id || entry.regie_id) && (
            <Button
              variant="outline"
              onClick={deleteSource}
              disabled={deletingSource}
              className="border-red-300 text-red-700 hover:bg-red-50 mr-auto"
              data-testid="delete-source-from-planner"
              title={entry.is_regie ? "Șterge regia și toate alocările" : "Șterge proiectul și toate alocările"}
            >
              <Trash2 size={14} className="mr-1.5"/>
              {deletingSource ? "Se șterge…" : (entry.is_regie ? "Șterge regia" : "Șterge proiectul")}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={save} disabled={saving} data-testid="save-assignments" className="!text-red-600 font-bold">{saving ? "Se salvează…" : "Salvează alocări"}</Button>
        </DialogFooter>
      </DialogContent>

      {/* Dialog conflict alocare persoană */}
      <Dialog open={!!pendingConflict} onOpenChange={(v) => !v && setPendingConflict(null)}>
        <DialogContent className="max-w-md" data-testid="conflict-person-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">⚠️</span>
              Persoana e alocată deja
            </DialogTitle>
          </DialogHeader>
          {pendingConflict && (
            <div className="space-y-3">
              <div className="text-sm">
                <strong className="text-zinc-900">{pendingConflict.person?.name}</strong> are deja {pendingConflict.otherAssignments.length === 1 ? "o alocare" : `${pendingConflict.otherAssignments.length} alocări`} care se suprapune cu intervalul curent ({fmtRoDate(entry.start_date)} → {fmtRoDate(entry.end_date)}):
              </div>
              <ul className="text-xs space-y-1.5 bg-amber-50 border border-amber-200 rounded p-2 max-h-[200px] overflow-auto">
                {pendingConflict.otherAssignments.map((a) => (
                  <li key={a.schedule_id} className="flex items-start gap-2">
                    <span className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{ background: a.color || "#9CA3AF" }}/>
                    <div className="flex-1">
                      <div className="font-bold text-zinc-800">{a.label || "—"}</div>
                      <div className="text-zinc-600">{fmtRoDate(a.start_date)} → {fmtRoDate(a.end_date)}{a.is_regie ? " (regie)" : ""}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="text-xs text-zinc-700">Cum să procedăm?</div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setPendingConflict(null)} data-testid="conflict-cancel">Anulează</Button>
            <Button variant="outline" onClick={acceptConflictBoth} data-testid="conflict-both" className="border-amber-400 text-amber-900 hover:bg-amber-50">
              ⏱ Lucrează la ambele (split orar)
            </Button>
            <Button onClick={acceptConflictMove} data-testid="conflict-move" className="bg-[var(--primary)]">
              🔄 Mută din alocările anterioare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
